import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FinancialConfig } from './FinancialConfig.js';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';
import CalculationTransparency from './components/CalculationTransparency.js';

const SMSFDownsizerContribution = ({ smsfState, setSmsfState }) => {
    const [activeTab, setActiveTab] = useState('calculator');
    
    // Downsizer contribution constants from FinancialConfig
    const DOWNSIZER = FinancialConfig.CONTRIBUTION_CAPS.DOWNSIZER;

    const [downsizerData, setDownsizerData] = useState({
        age: 65,
        salePrice: 800000,
        purchasePrice: 200000,
        capitalGain: 600000,
        ownershipYears: 25,
        settlementDate: new Date().toISOString().split('T')[0],
        contributionDate: new Date().toISOString().split('T')[0],
        homeType: 'main_residence', // main_residence, investment
        ownershipPeriod: '10_years_plus', // under_10, 10_years_plus
        partialExemption: false,
        partialExemptionYears: 0,
        spouse: {
            hasSpouse: true,
            age: 63,
            eligible: true,
            contributionAmount: 300000
        },
        proposedContribution: 300000,
        totalSuperBalance: 500000,
        otherContributions: 0
    });

    const [results, setResults] = useState(null);

    const calculateDownsizer = () => {
        const errors = [];
        const warnings = [];

        // Age eligibility
        if (downsizerData.age < DOWNSIZER.MIN_AGE) {
            errors.push({
                type: 'age',
                message: `Must be at least ${DOWNSIZER.MIN_AGE} years old`,
                value: downsizerData.age
            });
        }

        // Ownership period
        if (downsizerData.ownershipYears < DOWNSIZER.MIN_OWNERSHIP_YEARS) {
            errors.push({
                type: 'ownership',
                message: `Must have owned the property for at least ${DOWNSIZER.MIN_OWNERSHIP_YEARS} years`,
                value: `${downsizerData.ownershipYears} years`
            });
        }

        // Settlement and contribution dates
        const settlement = new Date(downsizerData.settlementDate);
        const contribution = new Date(downsizerData.contributionDate);
        const daysDifference = Math.floor((contribution - settlement) / (1000 * 60 * 60 * 24));
        
        if (daysDifference > DOWNSIZER.CONTRIBUTION_DEADLINE_DAYS) {
            errors.push({
                type: 'timing',
                message: `Contribution must be made within ${DOWNSIZER.CONTRIBUTION_DEADLINE_DAYS} days of settlement`,
                value: `${daysDifference} days`
            });
        } else if (daysDifference > 60) {
            warnings.push({
                type: 'timing',
                message: `You have ${DOWNSIZER.CONTRIBUTION_DEADLINE_DAYS - daysDifference} days remaining to make your contribution`,
                severity: 'warning'
            });
        }

        // Main residence requirement
        if (downsizerData.homeType !== 'main_residence') {
            errors.push({
                type: 'property_type',
                message: 'Property must be your main residence (or former main residence) in Australia',
                value: 'Not main residence'
            });
        }

        // Contribution amount
        const maxContribution = Math.min(
            DOWNSIZER.MAX_CONTRIBUTION_PER_PERSON,
            downsizerData.salePrice - downsizerData.otherContributions
        );

        if (downsizerData.proposedContribution > maxContribution) {
            errors.push({
                type: 'amount',
                message: `Contribution cannot exceed $${DOWNSIZER.MAX_CONTRIBUTION_PER_PERSON.toLocaleString()} or sale proceeds`,
                value: `$${downsizerData.proposedContribution.toLocaleString()}`
            });
        }

        // CGT implications
        const cgtExemption = downsizerData.partialExemption 
            ? (downsizerData.partialExemptionYears / downsizerData.ownershipYears) * downsizerData.capitalGain
            : downsizerData.capitalGain;

        // Spouse contribution
        let spouseContribution = 0;
        if (downsizerData.spouse.hasSpouse && downsizerData.spouse.eligible) {
            if (downsizerData.spouse.age >= DOWNSIZER.MIN_AGE) {
                spouseContribution = Math.min(
                    downsizerData.spouse.contributionAmount,
                    DOWNSIZER.MAX_CONTRIBUTION_PER_PERSON
                );
            } else {
                warnings.push({
                    type: 'spouse',
                    message: `Spouse must be at least ${DOWNSIZER.MIN_AGE} years old to be eligible`,
                    severity: 'warning'
                });
            }
        }

        // Total family contribution
        const totalFamilyContribution = downsizerData.proposedContribution + spouseContribution;

        // Benefits calculation
        const benefits = {
            exemptFromWorkTest: DOWNSIZER.EXEMPT_FROM_WORK_TEST,
            exemptFromAgeLimit: DOWNSIZER.EXEMPT_FROM_AGE_LIMIT,
            exemptFromTSBTest: DOWNSIZER.EXEMPT_FROM_TSB_TEST,
            exemptFromContributionCaps: DOWNSIZER.EXEMPT_FROM_CAPS,
            cgtExemptAmount: cgtExemption,
            taxFreeWithdrawal: true // Can be withdrawn tax-free at 60+
        };

        // Future value calculation
        const futureValue = calculateFutureValue(totalFamilyContribution, downsizerData.age);

        // Alternative uses
        const alternatives = calculateAlternatives(
            downsizerData.proposedContribution,
            downsizerData.age
        );

        setResults({
            eligible: errors.length === 0,
            errors,
            warnings,
            personalContribution: downsizerData.proposedContribution,
            spouseContribution,
            totalFamilyContribution,
            maxAllowed: maxContribution,
            remaining: maxContribution - downsizerData.proposedContribution,
            daysTillDeadline: Math.max(0, DOWNSIZER.SALE_PERIOD_DAYS - daysDifference),
            benefits,
            cgtSaving: cgtExemption * 0.235, // Assume top marginal rate
            futureValue,
            alternatives
        });
        setActiveTab('results');
    };

    const calculateFutureValue = (contribution, age) => {
        const yearsToRetirement = Math.max(0, 67 - age);
        const returnRate = 0.07;
        const futureValue = contribution * Math.pow(1 + returnRate, yearsToRetirement);

        return {
            contribution,
            years: yearsToRetirement,
            returnRate: returnRate * 100,
            futureValue: Math.round(futureValue),
            growth: Math.round(futureValue - contribution)
        };
    };

    const calculateAlternatives = (amount, age) => {
        // Alternative 1: Pay off mortgage
        const mortgageInterestSaved = amount * 0.06 * 10; // Assume 6% over 10 years

        // Alternative 2: Invest outside super
        const outsideSuperGrowth = amount * Math.pow(1.05, 10); // Lower return, taxed

        // Alternative 3: Keep in bank
        const bankGrowth = amount * Math.pow(1.02, 10); // 2% p.a.

        return [
            {
                option: 'Downsizer to Super',
                value: amount * Math.pow(1.07, 10),
                tax: 0, // Tax-free if over 60
                description: '7% return p.a., tax-free withdrawals at 60+'
            },
            {
                option: 'Pay Off Mortgage',
                value: amount + mortgageInterestSaved,
                tax: 0,
                description: '6% saving on mortgage interest'
            },
            {
                option: 'Invest Outside Super',
                value: outsideSuperGrowth * 0.85, // After CGT
                tax: outsideSuperGrowth * 0.15,
                description: '5% return p.a., subject to CGT'
            },
            {
                option: 'Bank Savings',
                value: bankGrowth * 0.53, // After income tax
                tax: (bankGrowth - amount) * 0.47,
                description: '2% p.a., interest taxed at marginal rate'
            }
        ];
    };

    useEffect(() => {
        calculateDownsizer();
    }, [downsizerData]);

    const renderCalculator = () => (
        <div className="space-y-6">
            {/* Eligibility Checklist */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                <h3 className="font-semibold text-blue-900 mb-2">✓ Eligibility Requirements</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Be at least {DOWNSIZER.MIN_AGE} years old</li>
                    <li>• Sell your main residence (or former main residence)</li>
                    <li>• Owned the property for at least {DOWNSIZER.MIN_OWNERSHIP_PERIOD} years</li>
                    <li>• Contribute within {DOWNSIZER.SALE_PERIOD_DAYS} days of settlement</li>
                    <li>• Property must be in Australia</li>
                    <li>• Not previously used downsizer scheme for another property</li>
                </ul>
            </div>

            {/* Personal Details */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">Personal Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Your Age
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={downsizerData.age}
                            onChange={(e) => setDownsizerData({ ...downsizerData, age: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Total Super Balance
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={downsizerData.totalSuperBalance}
                            onChange={(e) => setDownsizerData({ ...downsizerData, totalSuperBalance: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                </div>
            </div>

            {/* Property Details */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">Property Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Sale Price
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={downsizerData.salePrice}
                            onChange={(e) => setDownsizerData({ ...downsizerData, salePrice: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Ownership Period (years)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={downsizerData.ownershipYears}
                            onChange={(e) => setDownsizerData({ ...downsizerData, ownershipYears: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Settlement Date
                        </label>
                        <input
                            type="date"
                            value={downsizerData.settlementDate}
                            onChange={(e) => setDownsizerData({ ...downsizerData, settlementDate: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Proposed Contribution Date
                        </label>
                        <input
                            type="date"
                            value={downsizerData.contributionDate}
                            onChange={(e) => setDownsizerData({ ...downsizerData, contributionDate: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                </div>
            </div>

            {/* Contribution Amount */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">Contribution Amount</h3>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Your Proposed Contribution
                    </label>
                    <input
                        {...getNumericInputProps()}
                        value={downsizerData.proposedContribution}
                        onChange={(e) => setDownsizerData({ ...downsizerData, proposedContribution: Number(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Maximum: ${DOWNSIZER.MAX_CONTRIBUTION_PER_PERSON.toLocaleString()} per person
                    </p>
                </div>
            </div>

            {/* Spouse Details */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">Spouse Details</h3>
                <div className="mb-4">
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            checked={downsizerData.spouse.hasSpouse}
                            onChange={(e) => setDownsizerData({
                                ...downsizerData,
                                spouse: { ...downsizerData.spouse, hasSpouse: e.target.checked }
                            })}
                            className="mr-2"
                        />
                        <span className="text-sm font-medium text-gray-700">Include spouse contribution</span>
                    </label>
                </div>
                {downsizerData.spouse.hasSpouse && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Spouse Age
                            </label>
                            <input
                                {...getNumericInputProps()}
                                value={downsizerData.spouse.age}
                                onChange={(e) => setDownsizerData({
                                    ...downsizerData,
                                    spouse: { ...downsizerData.spouse, age: Number(e.target.value) }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Spouse Contribution
                            </label>
                            <input
                                {...getNumericInputProps()}
                                value={downsizerData.spouse.contributionAmount}
                                onChange={(e) => setDownsizerData({
                                    ...downsizerData,
                                    spouse: { ...downsizerData.spouse, contributionAmount: Number(e.target.value) }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                    </div>
                )}
            </div>

            <button
                type="button"
                onClick={calculateDownsizer}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 font-semibold"
            >
                Calculate Downsizer Contribution
            </button>
        </div>
    );

    const renderResults = () => {
        if (!results) return null;

        return (
            <div className="space-y-6">
                {/* Eligibility Status */}
                {results.eligible ? (
                    <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-lg">
                        <h3 className="font-semibold text-green-900 text-lg mb-2">✅ Eligible for Downsizer Contribution!</h3>
                        <p className="text-green-800">
                            You meet all the requirements to make a downsizer contribution.
                        </p>
                        {results.daysTillDeadline > 0 && (
                            <p className="text-green-700 mt-2 font-semibold">
                                ⏰ You have {results.daysTillDeadline} days remaining to make your contribution
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
                        <h3 className="font-semibold text-red-900 text-lg mb-2">❌ Not Eligible</h3>
                        <ul className="space-y-1">
                            {results.errors.map((error, index) => (
                                <li key={index} className="text-red-800">• {error.message}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Results Cards */}
                {results.eligible && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
                                <div className="text-sm text-gray-600 mb-1">Your Contribution</div>
                                <div className="text-2xl font-bold text-blue-600">
                                    ${results.personalContribution.toLocaleString()}
                                </div>
                            </div>

                            <div className="bg-white p-6 rounded-lg shadow border-l-4 border-purple-500">
                                <div className="text-sm text-gray-600 mb-1">Spouse Contribution</div>
                                <div className="text-2xl font-bold text-purple-600">
                                    ${results.spouseContribution.toLocaleString()}
                                </div>
                            </div>

                            <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
                                <div className="text-sm text-gray-600 mb-1">Total Family Contribution</div>
                                <div className="text-2xl font-bold text-green-600">
                                    ${results.totalFamilyContribution.toLocaleString()}
                                </div>
                            </div>
                        </div>

                        {/* Key Benefits */}
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg shadow">
                            <h3 className="font-semibold text-lg mb-4">🎁 Key Benefits</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-start">
                                    <span className="text-green-500 mr-2">✓</span>
                                    <div>
                                        <div className="font-semibold">Exempt from contribution caps</div>
                                        <div className="text-sm text-gray-700">Up to $300,000 per person, outside normal limits</div>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <span className="text-green-500 mr-2">✓</span>
                                    <div>
                                        <div className="font-semibold">No work test required</div>
                                        <div className="text-sm text-gray-700">Contribute regardless of employment status</div>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <span className="text-green-500 mr-2">✓</span>
                                    <div>
                                        <div className="font-semibold">No age limit</div>
                                        <div className="text-sm text-gray-700">Can contribute at any age above 55</div>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <span className="text-green-500 mr-2">✓</span>
                                    <div>
                                        <div className="font-semibold">Exempt from TSB test</div>
                                        <div className="text-sm text-gray-700">No super balance restrictions</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Future Value */}
                        <div className="bg-white p-6 rounded-lg shadow">
                            <h3 className="font-semibold text-lg mb-4">📈 Future Value</h3>
                            <p className="text-gray-700 mb-4">
                                With compound growth over {results.futureValue.years} years at {results.futureValue.returnRate}% p.a., 
                                your ${results.futureValue.contribution.toLocaleString()} could grow to:
                            </p>
                            <div className="text-4xl font-bold text-green-600 mb-2">
                                ${results.futureValue.futureValue.toLocaleString()}
                            </div>
                            <div className="text-sm text-gray-600">
                                That's ${results.futureValue.growth.toLocaleString()} in investment growth!
                            </div>
                        </div>

                        {/* Alternative Uses */}
                        <div className="bg-white p-6 rounded-lg shadow">
                            <h3 className="font-semibold text-lg mb-4">💡 Comparing Options (10-year projection)</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b">
                                            <th className="text-left py-2">Option</th>
                                            <th className="text-right py-2">After-tax Value</th>
                                            <th className="text-left py-2">Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {results.alternatives.map((alt, index) => (
                                            <tr key={index} className={`border-b ${index === 0 ? 'bg-green-50 font-semibold' : ''}`}>
                                                <td className="py-3">{alt.option}</td>
                                                <td className="text-right py-3">${Math.round(alt.value).toLocaleString()}</td>
                                                <td className="text-sm text-gray-600 py-3">{alt.description}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {/* Warnings */}
                {results.warnings.length > 0 && (
                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Warnings</h4>
                        <ul className="space-y-1">
                            {results.warnings.map((warning, index) => (
                                <li key={index} className="text-sm text-yellow-800">• {warning.message}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <CalculationTransparency
                    kind="smsf"
                    title="Downsizer Transparency"
                    inputs={{
                        currentAge: downsizerData.age,
                        contributionAmount: downsizerData.proposedContribution,
                        totalBalance: downsizerData.totalSuperBalance,
                    }}
                    results={{
                        ...results,
                        tableData: results.alternatives || [],
                    }}
                />
            </div>
        );
    };

    const renderEducation = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">📚 About Downsizer Contributions</h3>
                
                <div className="space-y-4">
                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">What is it?</h4>
                        <p className="text-gray-700">
                            The downsizer contribution allows you to contribute up to $300,000 from the proceeds of selling 
                            your home into superannuation. This is outside the normal contribution caps and is available 
                            from age {DOWNSIZER.MIN_AGE}.
                        </p>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-green-900 mb-2">Why Use It?</h4>
                        <ul className="list-disc list-inside space-y-1 text-green-800">
                            <li>Get up to $600,000 per couple into super outside caps</li>
                            <li>No work test or age restrictions (if over 55)</li>
                            <li>Tax-effective way to boost retirement savings</li>
                            <li>Withdrawals tax-free at 60+</li>
                            <li>Can help qualify for age pension (if done strategically)</li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Key Requirements</h4>
                        <div className="space-y-2 text-sm">
                            <div className="flex items-start">
                                <span className="font-semibold w-32 flex-shrink-0">Age:</span>
                                <span>{DOWNSIZER.MIN_AGE} years or older</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-semibold w-32 flex-shrink-0">Ownership:</span>
                                <span>{DOWNSIZER.MIN_OWNERSHIP_YEARS}+ years before sale</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-semibold w-32 flex-shrink-0">Property:</span>
                                <span>Main residence in Australia</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-semibold w-32 flex-shrink-0">Timing:</span>
                                <span>Contribute within {DOWNSIZER.CONTRIBUTION_DEADLINE_DAYS} days of settlement</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-semibold w-32 flex-shrink-0">Amount:</span>
                                <span>Up to ${DOWNSIZER.MAX_CONTRIBUTION_PER_PERSON.toLocaleString()} per person</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-semibold w-32 flex-shrink-0">One-time:</span>
                                <span>Can only use once in your lifetime</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Important Considerations</h4>
                        <ul className="list-disc list-inside space-y-1 text-yellow-800 text-sm">
                            <li>Once contributed, money is locked until preservation age</li>
                            <li>May affect age pension eligibility (assets test)</li>
                            <li>Cannot be reversed or withdrawn early (except hardship)</li>
                            <li>Must complete ATO form and notify super fund</li>
                            <li>Both spouses can contribute ($600,000 total per couple)</li>
                            <li>Doesn't count towards contribution caps</li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Example Scenario</h4>
                        <div className="bg-blue-50 p-4 rounded-lg text-sm">
                            <p className="mb-2"><strong>John and Mary (both 67) sell their home:</strong></p>
                            <ul className="space-y-1 text-blue-900">
                                <li>• Sale price: $1,200,000</li>
                                <li>• Owned for 30 years</li>
                                <li>• Each contributes $300,000 to super</li>
                                <li>• Total: $600,000 boosting retirement savings</li>
                                <li>• Can access tax-free immediately (over 60)</li>
                                <li>• Remaining $600,000 for new home or other uses</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto p-6">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Downsizer Contribution Calculator</h2>
                <p className="text-gray-600">
                    Calculate your eligibility to contribute home sale proceeds to super
                </p>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="flex space-x-8">
                    {[
                        { id: 'calculator', label: 'Calculator', icon: '🏠' },
                        { id: 'results', label: 'Results', icon: '📊' },
                        { id: 'education', label: 'How It Works', icon: '📚' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <span className="mr-2">{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Content */}
            <div>
                {activeTab === 'calculator' && renderCalculator()}
                {activeTab === 'results' && renderResults()}
                {activeTab === 'education' && renderEducation()}
            </div>
        </div>
    );
};

SMSFDownsizerContribution.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired
};

export default SMSFDownsizerContribution;

